import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { Plus, Trash2, Eye, EyeOff, Copy, Check, Power, PowerOff, Key } from "lucide-react";

const API = `${window.location.origin}/api`;

function formatDt(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "-"; }
}

export default function ApiKeysPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [newKeyValue, setNewKeyValue] = useState(null);
  const [revealed, setRevealed] = useState({});
  const [copied, setCopied] = useState({});
  const [actionId, setActionId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const fetchKeys = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api-keys`, { headers });
      setKeys(res.data);
    } catch { toast.error("API anahtarları yüklenemedi"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchKeys(); }, []); // eslint-disable-line

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/api-keys`, { name: newName.trim() }, { headers });
      setNewKeyValue(res.data.key);
      toast.success("API anahtarı oluşturuldu");
      setNewName("");
      setShowCreate(false);
      fetchKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Oluşturma başarısız");
    } finally { setCreating(false); }
  };

  const handleToggle = async (id) => {
    setActionId(id);
    try {
      await axios.put(`${API}/api-keys/${id}/toggle`, {}, { headers });
      fetchKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || "İşlem başarısız");
    } finally { setActionId(null); }
  };

  const handleDelete = async (id) => {
    setActionId(id);
    try {
      await axios.delete(`${API}/api-keys/${id}`, { headers });
      toast.success("API anahtarı silindi");
      setConfirmDeleteId(null);
      fetchKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silme başarısız");
    } finally { setActionId(null); }
  };

  const copyKey = async (key, id) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopied((c) => ({ ...c, [id]: true }));
      setTimeout(() => setCopied((c) => ({ ...c, [id]: false })), 2000);
    } catch { toast.error("Panoya kopyalanamadı"); }
  };

  const maskedKey = (key) => {
    if (!key || key.length < 8) return "••••••••";
    return key.slice(0, 8) + "••••••••••••••••" + key.slice(-4);
  };

  return (
    <Layout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold uppercase text-gray-900 tracking-tight"
              style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
              API Anahtarları
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Kommo / Webhook entegrasyonu için API anahtarları
            </p>
          </div>
          <button
            data-testid="create-key-btn"
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-bold uppercase tracking-wide rounded-md transition-all active:scale-95"
            style={{ fontFamily: "Barlow Condensed, sans-serif" }}
          >
            <Plus className="w-4 h-4" /> Yeni Anahtar
          </button>
        </div>

        {/* Create form */}
        {showCreate && (
          <div className="mb-6 p-5 bg-white border border-gray-200 rounded-lg shadow-sm animate-fade-in-up">
            <h3 className="text-sm font-bold uppercase text-gray-700 tracking-wider mb-3">Yeni API Anahtarı</h3>
            <form onSubmit={handleCreate} className="flex gap-3">
              <input
                data-testid="create-key-name-input"
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#DC2626]"
                placeholder="Örn: Kommo CRM, Webhook Prod, Test"
                autoFocus
              />
              <button data-testid="create-key-confirm-btn" type="submit"
                disabled={creating || !newName.trim()}
                className="px-4 py-2 bg-[#DC2626] text-white text-sm font-bold rounded-md hover:bg-[#B91C1C] disabled:opacity-50">
                {creating ? "..." : "Oluştur"}
              </button>
              <button type="button" onClick={() => { setShowCreate(false); setNewName(""); }}
                className="px-4 py-2 border border-gray-300 text-gray-600 text-sm rounded-md hover:bg-gray-50">
                İptal
              </button>
            </form>
          </div>
        )}

        {/* New key reveal banner */}
        {newKeyValue && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg animate-fade-in-up">
            <p className="text-xs font-bold uppercase text-amber-700 tracking-wider mb-2">
              Yeni API Anahtarınız — Yalnızca bir kez gösterilecek!
            </p>
            <div className="flex items-center gap-2 bg-white border border-amber-200 rounded-md px-3 py-2">
              <code className="flex-1 text-sm font-mono text-gray-800 break-all">{newKeyValue}</code>
              <button
                data-testid="copy-new-key-btn"
                onClick={() => copyKey(newKeyValue, "new")}
                className="p-1.5 text-amber-600 hover:text-amber-800 shrink-0"
                title="Kopyala"
              >
                {copied["new"] ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <button onClick={() => setNewKeyValue(null)}
              className="mt-2 text-xs text-amber-600 hover:underline">
              Tamam, sakladım
            </button>
          </div>
        )}

        {/* API Usage info */}
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-xs font-bold uppercase text-gray-500 tracking-wider mb-2">API Kullanımı</p>
          <div className="space-y-1">
            <p className="text-xs text-gray-600 font-mono">
              <span className="text-gray-400">Header: </span>Authorization: Bearer {'<API_KEY>'}
            </p>
            <p className="text-xs text-gray-600 font-mono">
              <span className="text-gray-400">Query: </span>POST /api/price?key={'<API_KEY>'}
            </p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-8 h-8 border-2 border-[#DC2626] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : keys.length === 0 ? (
            <div className="py-16 text-center text-gray-400">
              <Key className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">Henüz API anahtarı yok.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="api-keys-table">
                <thead>
                  <tr className="bg-gray-50 text-xs font-bold uppercase tracking-wider text-gray-500 border-b">
                    <th className="px-5 py-3 text-left">Ad</th>
                    <th className="px-5 py-3 text-left">API Anahtarı</th>
                    <th className="px-5 py-3 text-left">Durum</th>
                    <th className="px-5 py-3 text-right">İstek Sayısı</th>
                    <th className="px-5 py-3 text-left">Son Kullanım</th>
                    <th className="px-5 py-3 text-left">Oluşturulma</th>
                    <th className="px-5 py-3 text-right">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {keys.map((k) => (
                    <tr key={k.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3.5 font-semibold text-gray-800">{k.name}</td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <code className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded max-w-[200px] truncate">
                            {revealed[k.id] ? k.key : maskedKey(k.key)}
                          </code>
                          <button
                            data-testid={`reveal-key-btn-${k.id}`}
                            onClick={() => setRevealed((r) => ({ ...r, [k.id]: !r[k.id] }))}
                            className="p-1 text-gray-400 hover:text-gray-700 transition-colors"
                            title={revealed[k.id] ? "Gizle" : "Göster"}
                          >
                            {revealed[k.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                          </button>
                          <button
                            data-testid={`copy-key-btn-${k.id}`}
                            onClick={() => copyKey(k.key, k.id)}
                            className="p-1 text-gray-400 hover:text-gray-700 transition-colors"
                            title="Kopyala"
                          >
                            {copied[k.id] ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        {k.is_active ? (
                          <span className="text-xs font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded-sm">AKTİF</span>
                        ) : (
                          <span className="text-xs font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-sm">PASİF</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-right font-mono text-sm text-gray-600">{k.request_count?.toLocaleString("tr-TR") || 0}</td>
                      <td className="px-5 py-3.5 text-xs text-gray-500">{formatDt(k.last_used_at)}</td>
                      <td className="px-5 py-3.5 text-xs text-gray-500">{formatDt(k.created_at)}</td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            data-testid={`toggle-key-btn-${k.id}`}
                            onClick={() => handleToggle(k.id)}
                            disabled={actionId === k.id}
                            className={`p-1.5 rounded-md transition-colors ${k.is_active ? "text-gray-400 hover:text-yellow-600 hover:bg-yellow-50" : "text-gray-400 hover:text-green-600 hover:bg-green-50"}`}
                            title={k.is_active ? "Devre Dışı Bırak" : "Aktifleştir"}
                          >
                            {k.is_active ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
                          </button>
                          <button
                            data-testid={`delete-key-btn-${k.id}`}
                            onClick={() => setConfirmDeleteId(k.id)}
                            disabled={actionId === k.id}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            title="Sil"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Delete confirm */}
        {confirmDeleteId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full animate-fade-in-up">
              <h3 className="text-lg font-bold text-gray-900 mb-2"
                style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                API Anahtarını Sil?
              </h3>
              <p className="text-sm text-gray-600 mb-5">
                Bu anahtar kullanılamaz hale gelecek. Bu işlem geri alınamaz.
              </p>
              <div className="flex gap-3">
                <button data-testid="confirm-delete-key-btn"
                  onClick={() => handleDelete(confirmDeleteId)}
                  disabled={actionId === confirmDeleteId}
                  className="flex-1 py-2 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-bold rounded-md disabled:opacity-50">
                  {actionId === confirmDeleteId ? "..." : "Sil"}
                </button>
                <button onClick={() => setConfirmDeleteId(null)}
                  className="flex-1 py-2 border border-gray-300 text-gray-600 text-sm rounded-md hover:bg-gray-50">
                  İptal
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
